import classnames from 'classnames';
import { ForwardedRef, forwardRef, PropsWithChildren, Ref, useMemo } from 'react';

import { chipGroupContext, ChipGroupContext } from '../ChipGroupItem/ChipGroupItem.types';

import styles from './ExclusiveChipGroup.module.scss';
import { ExclusiveChipGroupProps } from './ExclusiveChipGroup.types';

const ExclusiveChipGroupInner = <T extends string, TClearable extends boolean = false>(
  {
    size = 'medium',
    showCheckMark,
    clearable,
    value,
    onValueChange,
    className,
    children,
    disabled,
    error,
  }: PropsWithChildren<ExclusiveChipGroupProps<T, TClearable>>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const chipGroup = useMemo(
    (): ChipGroupContext => ({
      showCheckMark,
      size,
      selectedValues: [value].filter(Boolean),
      onSelectValue: clickedValue => {
        // @ts-expect-error We can call onValueChange with undefined because we know that clearable is true
        if (clearable && clickedValue === value) onValueChange?.(undefined);
        else onValueChange?.(clickedValue as T);
      },
      disabled,
      error,
    }),
    [clearable, disabled, error, onValueChange, showCheckMark, size, value]
  );

  return (
    <chipGroupContext.Provider value={chipGroup}>
      <div className={classnames(styles.exclusiveChipGroup, className)} ref={ref}>
        {children}
      </div>
    </chipGroupContext.Provider>
  );
};

export const ExclusiveChipGroup = forwardRef(ExclusiveChipGroupInner) as <
  T extends string,
  TClearable extends boolean = false,
>(
  props: PropsWithChildren<ExclusiveChipGroupProps<T, TClearable>> & { ref?: Ref<HTMLDivElement> }
) => ReturnType<typeof ExclusiveChipGroupInner>;
